import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../app/store/slices/uiSlice";
import { ArrowLeftIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ConfirmModal } from "../ui";
import { FormButtons } from "../ui/form";
import DOMPurify from "dompurify";
import CRUDFormikForm from "./CRUDFormikForm";

export default function CRUDDetailView({
  title,
  viewState,
  formFields,
  error,
  showSuccess,
  isSubmitting,
  isDeleting,
  toast,
  customHandlers,
  activeTab,
  onBackToList,
  onEditFromView,
  onDelete,
  onSubmit,
  onCancel,
  setActiveTab,
  confirmationModal,
  onConfirmDelete,
  onCancelDelete,
  getDisplayName,
  transferId,
  setTransferId,
  multiStepSubmission,
  onRedirectToEdit,
  config = {},
}) {
  const darkMode = useSelector(selectDarkMode);
  const [tabSubmitting, setTabSubmitting] = useState(false);
  const [localToast, setLocalToast] = useState({ show: false, message: "", type: "error" });

  // Show error toast
  const showErrorToast = (message) => {
    setLocalToast({ show: true, message, type: "error" });
    setTimeout(() => {
      setLocalToast({ show: false, message: "", type: "error" });
    }, 5000);
  };

  // Get unique sections/tabs from form fields
  const sections = [...new Set(formFields.map((field) => field.section || "basic"))];
  const hasMultipleTabs = sections.length > 1;
  const currentTabIndex = sections.indexOf(activeTab);
  const isFirstTab = currentTabIndex === 0;
  const isLastTab = currentTabIndex === sections.length - 1;

  // Handle next tab with Formik validation
  const handleNextWithFormik = async (formikProps) => {
    const { validateForm, setTouched, values } = formikProps;

    // Get current tab fields
    const currentTabFields = formFields.filter((field) => field.section === activeTab);
    const fieldNames = currentTabFields.map((field) => field.name);

    // Touch all fields in current tab to show errors
    const touchedFields = {};
    fieldNames.forEach((name) => {
      touchedFields[name] = true;
    });
    setTouched(touchedFields, false);

    // Validate form
    const errors = await validateForm(values);

    // Check if any current tab fields have errors
    const hasCurrentTabErrors = fieldNames.some((name) => errors[name]);

    if (hasCurrentTabErrors) {
      return; // Don't proceed to next tab
    }

    // Get the next tab
    const nextTab = currentTabIndex < sections.length - 1 ? sections[currentTabIndex + 1] : null;

    // Check if we need to call onTabSubmit for multi-step submission
    // For the last tab (inspection), nextTab will be null but we still need to call onTabSubmit
    if (multiStepSubmission && customHandlers?.onTabSubmit && (nextTab || isLastTab)) {
      // Define which tab transitions require API calls
      // In edit mode: all tabs require API call (PUT) - including inspection (last tab)
      // In add mode: only handling, receive, and inspection tabs require API call
      const tabsRequiringSubmission = viewState.mode === "edit" ? ["vaccine", "route", "handling", "receive", "inspection"] : ["handling", "receive", "inspection"];

      if (tabsRequiringSubmission.includes(activeTab)) {
        setTabSubmitting(true);
        try {
          const result = await customHandlers.onTabSubmit(activeTab, nextTab, values, viewState.mode, transferId);

          // Update transferId if a new one was created
          console.log("Tab submit result:", result, "viewState.mode:", viewState.mode);

          if (result?.transferId && setTransferId) {
            console.log("Setting transferId:", result.transferId);
            setTransferId(result.transferId);

            // In add mode, redirect to edit page after creating transfer
            // This ensures data persists on page reload
            if (viewState.mode === "add" && onRedirectToEdit) {
              console.log("Redirecting to edit page with transferId:", result.transferId, "nextTab:", nextTab);
              onRedirectToEdit(result.transferId, nextTab);
              return;
            }
          }

          // If inspection is complete, redirect back to list
          if (result?.isComplete) {
            console.log("Inspection complete, redirecting to list");
            onBackToList();
            return;
          }

          // Move to next tab on success (only if there's a next tab)
          if (nextTab) {
            console.log("Moving to next tab:", nextTab);
            setActiveTab(nextTab);
          }
        } catch (error) {
          console.error("Tab submission error:", error);
          // Extract error message from API response
          const errorMessage = error?.response?.data?.message || error?.message || "An error occurred. Please try again.";
          showErrorToast(errorMessage);
        } finally {
          setTabSubmitting(false);
        }
        return;
      }
    }

    // Move to next tab (default behavior)
    if (nextTab) {
      setActiveTab(nextTab);
    } else if (isLastTab && !multiStepSubmission) {
      // For regular tabbed forms (not multi-step), submit the form on last tab
      formikProps.handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentTabIndex > 0) {
      setActiveTab(sections[currentTabIndex - 1]);
    }
  };

  const getTitle = () => {
    if (viewState.mode === "add") return `Add New ${title}`;
    if (viewState.mode === "edit") return `Edit ${title}`;
    return `${title} Details`;
  };

  return (
    <div className="p-5 sm:p-6">
      {showSuccess && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          {title} {viewState.mode === "add" ? "added" : "updated"} successfully!
        </div>
      )}

      {toast?.show && <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${toast.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"}`}>{toast.message}</div>}

      {localToast.show && <div className="fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 bg-red-500 text-white">{localToast.message}</div>}

      <div className={`rounded-xl p-4 shadow-sm border mb-5 sm:mb-6 ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={onBackToList} className={`flex items-center transition-colors ${darkMode ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900"}`}>
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
            </button>
            <h1 className={`text-xl sm:text-2xl font-semibold ${darkMode ? "text-white" : "text-[#232526]"}`}>{getTitle()}</h1>
          </div>

          {viewState.mode === "view" && (
            <div className="flex items-center space-x-3">
              {config?.showEditButton !== false && (
                <button onClick={onEditFromView} className="inline-flex items-center px-4 py-2 rounded-xl text-white font-semibold bg-[linear-gradient(180deg,#4A9CFF_0%,#4057FE_100%)] hover:opacity-90 transition-opacity shadow-sm">
                  <PencilIcon className="w-5 h-5 mr-2" />
                  Edit {title}
                </button>
              )}
              {config?.showDeleteButton !== false && (
                <button onClick={() => onDelete(viewState.item)} className={`flex items-center justify-center w-[30px] h-[30px] rounded transition-opacity hover:opacity-80 ${darkMode ? "bg-red-900/30 text-red-400" : "bg-[#FF3B3012] text-[#FF3B30]"}`}>
                  <TrashIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {viewState.mode === "view" && viewState.item && (
        <>
          <div className={`rounded-xl shadow-sm border ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"}`}>
            <div className="p-8">
              <div className="grid grid-cols-3 gap-x-12 gap-y-8">
                {[...formFields.filter((field) => field.name === "profilePicture" || field.type === "file"), ...formFields.filter((field) => field.name !== "profilePicture" && field.type !== "file" && field.name !== "children")].map((field) => {
                  const value = viewState.item[field.name];

                  if ((field.type === "file" || field.name === "profilePicture") && value) {
                    const displayUrl = value?.startsWith("https://localhost") ? value.replace("https://localhost", "http://localhost") : value;
                    return (
                      <div key={field.name}>
                        <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{field.label}</label>
                        <img src={displayUrl} alt={field.label} className={`h-28 w-28 rounded-full object-cover border shadow-sm ${darkMode ? "border-gray-600" : ""}`} />
                      </div>
                    );
                  }

                  if (field.name === "numberOfChildren") {
                    return (
                      <div key={field.name}>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>{field.label}</label>
                        <div className={`text-base ${darkMode ? "text-white" : "text-[#232526]"}`}>{Array.isArray(viewState.item.children) ? viewState.item.children.length : value || "0"}</div>
                      </div>
                    );
                  }

                  return (
                    <div key={field.name}>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>{field.label}</label>
                      <div className={`text-base ${darkMode ? "text-white" : "text-[#232526]"}`}>
                        {field.type === "richtext" ? (
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value || "—") }} />
                        ) : field.renderValue ? (
                          field.renderValue(value, viewState.item)
                        ) : (
                          (typeof value === "object" && value !== null ? JSON.stringify(value) : value) || "—"
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Children Section */}
          {Array.isArray(viewState.item.children) && viewState.item.children.length > 0 && (
            <div className={`rounded-xl shadow-sm border mt-6 ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"}`}>
              <div className={`px-8 py-4 border-b ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
                <h3 className={`text-lg font-semibold ${darkMode ? "text-white" : "text-[#232526]"}`}>Children Information ({viewState.item.children.length})</h3>
              </div>
              <div className="p-8">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
                        <th className={`text-left py-3 px-4 font-medium ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>Child</th>
                        <th className={`text-left py-3 px-4 font-medium ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>Date of Birth</th>
                        <th className={`text-left py-3 px-4 font-medium ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>Gender</th>
                        <th className={`text-left py-3 px-4 font-medium ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>Blood Group</th>
                        <th className={`text-left py-3 px-4 font-medium ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>Weight</th>
                        <th className={`text-left py-3 px-4 font-medium ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>Height</th>
                        <th className={`text-left py-3 px-4 font-medium ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>Allergies</th>
                        <th className={`text-left py-3 px-4 font-medium ${darkMode ? "text-gray-400" : "text-[#74777F]"}`}>Medical Conditions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewState.item.children.map((child, index) => (
                        <tr key={child.id || index} className={`border-b ${darkMode ? "border-gray-700" : "border-gray-100"}`}>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-3">
                              {child.profilePhoto ? (
                                <img src={child.profilePhoto} alt={child.name} className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                                  <span className="text-white text-sm font-medium">{child.name?.charAt(0) || "?"}</span>
                                </div>
                              )}
                              <span className={`font-medium ${darkMode ? "text-white" : "text-[#232526]"}`}>{child.name || "—"}</span>
                            </div>
                          </td>
                          <td className={`py-4 px-4 ${darkMode ? "text-gray-300" : "text-[#232526]"}`}>{child.dateOfBirth ? new Date(child.dateOfBirth).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                          <td className={`py-4 px-4 ${darkMode ? "text-gray-300" : "text-[#232526]"}`}>{child.gender || "—"}</td>
                          <td className={`py-4 px-4 ${darkMode ? "text-gray-300" : "text-[#232526]"}`}>{child.bloodGroup || "—"}</td>
                          <td className={`py-4 px-4 ${darkMode ? "text-gray-300" : "text-[#232526]"}`}>{child.weightKg ? `${child.weightKg} kg` : "—"}</td>
                          <td className={`py-4 px-4 ${darkMode ? "text-gray-300" : "text-[#232526]"}`}>{child.heightCm ? `${child.heightCm} cm` : "—"}</td>
                          <td className={`py-4 px-4 ${darkMode ? "text-gray-300" : "text-[#232526]"}`}>{Array.isArray(child.allergies) && child.allergies.length > 0 ? child.allergies.join(", ") : "—"}</td>
                          <td className={`py-4 px-4 ${darkMode ? "text-gray-300" : "text-[#232526]"}`}>{Array.isArray(child.medicalConditions) && child.medicalConditions.length > 0 ? child.medicalConditions.join(", ") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {(viewState.mode === "add" || viewState.mode === "edit") && (
        <div className={`rounded-xl sm:p-8 p-5 shadow-sm border ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"}`}>
          {error && <div className={`mb-4 p-3 border rounded ${darkMode ? "bg-red-900/30 border-red-800 text-red-400" : "bg-red-100 border-red-400 text-red-700"}`}>{error}</div>}
          <CRUDFormikForm
            formFields={formFields}
            initialData={viewState.mode === "edit" ? viewState.item : viewState.initialData || {}}
            onSubmit={onSubmit}
            customHandlers={customHandlers}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            mode={viewState.mode}
            transferId={transferId}
            renderButtons={(formikProps, isFormikSubmitting) => {
              // Check if Next button should be disabled on Receive tab
              const isReceiveTab = activeTab === "receive";
              const transferStatus = formikProps.values?.transferStatus;
              const isTransferNotArrived = isReceiveTab && transferStatus !== "ARRIVED";

              return (
                <FormButtons
                  onCancel={onCancel}
                  mode={viewState.mode === "add" ? "new" : "edit"}
                  cancelLabel="Cancel"
                  submitLabel={viewState.mode === "add" ? `Add ${title}` : `Update ${title}`}
                  hasMultipleTabs={hasMultipleTabs}
                  isFirstTab={isFirstTab}
                  isLastTab={isLastTab}
                  onNext={() => handleNextWithFormik(formikProps)}
                  onPrevious={handlePrevious}
                  isSubmitting={isFormikSubmitting || isSubmitting || tabSubmitting}
                  disableNext={isTransferNotArrived}
                  disableNextTooltip={isTransferNotArrived ? "Transfer Status must be 'ARRIVED' to proceed to Inspection" : null}
                />
              );
            }}
          />
        </div>
      )}

      <ConfirmModal isOpen={confirmationModal.isOpen} onCancel={onCancelDelete} onConfirm={onConfirmDelete} title={`Delete ${title}`} message={`Are you sure you want to delete "${getDisplayName(confirmationModal.item)}"? This action cannot be undone.`} isLoading={isDeleting} />
    </div>
  );
}
